/**
 * Convert an image File to WebP format using Canvas API.
 * Optionally resizes to fit within maxWidth/maxHeight while preserving aspect ratio.
 * Returns a new File with .webp extension.
 */
export function convertToWebP(
  file: File,
  quality = 0.82,
  maxWidth?: number,
  maxHeight?: number
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      let w = img.naturalWidth;
      let h = img.naturalHeight;

      // Resize if dimensions exceed max
      if (maxWidth || maxHeight) {
        const mw = maxWidth || Infinity;
        const mh = maxHeight || Infinity;
        if (w > mw || h > mh) {
          const ratio = Math.min(mw / w, mh / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
      }

      // Skip conversion if already WebP and no resize needed
      if (file.type === 'image/webp' && w === img.naturalWidth && h === img.naturalHeight) {
        URL.revokeObjectURL(url);
        resolve(file);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('WebP conversion failed'));
            return;
          }
          const baseName = file.name.replace(/\.[^.]+$/, '');
          const webpFile = new File([blob], `${baseName}.webp`, {
            type: 'image/webp',
          });
          resolve(webpFile);
        },
        'image/webp',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for conversion'));
    };

    img.src = url;
  });
}

/**
 * Returns the image URL as-is.
 */
export function getOptimizedImageUrl(
  url: string,
  _options?: { width?: number; height?: number; quality?: number }
): string {
  return url;
}
